import React, { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  CheckCircle,
  ExternalLink,
  ClipboardList,
  ArrowUp,
  ArrowDown,
  Eye,
  Wand2,
  GripVertical,
  FolderPlus,
  Folder,
  ChevronDown,
  ChevronRight,
  Settings,
  Paperclip,
  X,
  FileText,
} from "lucide-react";
import {
  PreConsultTemplate,
  Question,
  defaultSections,
  mockGPTQuestions,
  usePreConsultData,
} from "@/lib/settings/preConsultData";
import {
  setShowInviteTeamSideModal,
  setShowPermissionMessage,
  setShowPermissionModal,
} from "@/dispatchers/appDispatchers";
import { string } from "zod";
import { ObjectId } from "bson";
import EmailSMSSettingsSkeleton from "./skeletons/EmailSMSSettingsSkeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { JSONSchemaGenerator } from "zod/v4/core";
import { deleteConfirmationMessage } from "@/data/constantStrings";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessEmailTemplate,
  accessTitanverseTemplateUpsert,
} from "@/data/constantStrings";
// Component Props Types

interface PreConsultSettingsTabProps {
  templates: PreConsultTemplate[];
  onAddTemplate: () => void;
  onEditTemplate: (template: PreConsultTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  isDarkMode?: boolean;
}

interface PreConsultTemplateFormPanelProps {
  onSave: (templateData: PreConsultTemplate) => void;
  onProceedToBuilder?: (templateData: PreConsultTemplate) => void;
  onBack: () => void;
  isDarkMode?: boolean;
}

interface PreConsultTemplateBuilderPanelProps {
  template?: PreConsultTemplate;
  onSave: (templateData: PreConsultTemplate) => void;
  onBack: () => void;
  isDarkMode?: boolean;
  confirmDelete: (onConfirm: () => void, itemType?: string) => void;
}

interface QuestionFormPanelProps {
  question?: Question;
  sections: string[];
  onSave: (questionData: Question) => void;
  onBack: () => void;
}

interface SectionManagerProps {
  sections: string[];
  onSectionsUpdate: (
    sections: string[],
    oldSectionName?: string,
    newSectionName?: string,
  ) => void;
  isDarkMode?: boolean;
  onSectionsUpdateCallback: (sections: string[]) => void;
}
// NOTE: permission checks must run inside the component render
// so they evaluate after the store/app state is initialized.
// Section Manager Component
function SectionManager({
  sections,
  onSectionsUpdate,
  isDarkMode = false,
  onSectionsUpdateCallback,
}: SectionManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleAddSection = () => {
    if (newSectionName.trim() && !sections.includes(newSectionName.trim())) {
      onSectionsUpdate([...sections, newSectionName.trim()]);
      setNewSectionName("");
    }
  };

  const handleEditSection = (index: number) => {
    setEditingIndex(index);
    setEditingName(sections[index]);
  };

  const handleSaveEdit = () => {
    if (
      editingIndex !== null &&
      editingName.trim() &&
      !sections.includes(editingName.trim())
    ) {
      const oldSectionName = sections[editingIndex];
      const newSectionName = editingName.trim();

      const updatedSections = [...sections];
      updatedSections[editingIndex] = newSectionName;

      onSectionsUpdate(updatedSections, oldSectionName, newSectionName);

      setEditingIndex(null);
      setEditingName("");
    }
  };

  const handleDeleteSection = (index: number) => {
    if (sections.length > 1) {
      const updatedSections = sections.filter((_, i) => i !== index);
      onSectionsUpdate(updatedSections);
    }
  };

  const handleMoveSection = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < sections.length) {
      const updatedSections = [...sections];
      [updatedSections[index], updatedSections[newIndex]] = [
        updatedSections[newIndex],
        updatedSections[index],
      ];
      onSectionsUpdate(updatedSections);
      onSectionsUpdateCallback(updatedSections);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings className="w-4 h-4 mr-2" />
          Manage Sections
        </Button>
      </DialogTrigger>
      <DialogContent
        className={`max-w-md ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle className={isDarkMode ? "text-white" : "text-gray-900"}>
            Manage Form Sections
          </DialogTitle>
          <DialogDescription
            className={isDarkMode ? "text-gray-300" : "text-gray-600"}
          >
            Add, edit, and reorder the sections that organize your questions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Sections */}
          <div className="space-y-2">
            {sections.map((section, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 p-3 border rounded-lg ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <GripVertical
                  className={`w-4 h-4 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />

                {editingIndex === index ? (
                  <div className="flex flex-1 gap-2">
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="text-sm"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSaveEdit();
                        } else if (e.key === "Escape") {
                          setEditingIndex(null);
                          setEditingName("");
                        }
                      }}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={!editingName.trim()}
                    >
                      <Save className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <span
                      className={`flex-1 text-sm ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      {section}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveSection(index, "up")}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveSection(index, "down")}
                        disabled={index === sections.length - 1}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSection(index)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSection(index)}
                        disabled={sections.length <= 1}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Add New Section */}
          <div className="flex gap-2">
            <Input
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="New section name..."
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddSection();
                }
              }}
            />
            <Button
              onClick={handleAddSection}
              disabled={!newSectionName.trim()}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => setIsOpen(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Pre-Consult Template Settings Component
export function PreConsultSettingsTab({
  templates,
  onAddTemplate,
  onEditTemplate,
  onDeleteTemplate,
}: PreConsultSettingsTabProps) {
  const { isPreConsultTemplatesLoading, darkMode: isDarkMode } =
    usePreConsultData();

  // Evaluate upsert permission at render-time (not module-load)
  const canUpsertTemplate = checkUserPermissions(
    accessTitanverseTemplateUpsert,
  );

  if (isPreConsultTemplatesLoading) {
    return <EmailSMSSettingsSkeleton />;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className={`text-xl font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Pre-Consult Templates
          </h2>
          <p
            className={`text-sm mt-1 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Create forms for patients to fill out before their consultation.
            Patients receive a link to complete these forms prior to their
            appointment.
          </p>
        </div>
        <Button
          onClick={() => {
            if (canUpsertTemplate) {
              onAddTemplate();
            } else {
              setShowPermissionMessage("Create or update template");
              setShowPermissionModal(true);
            }
          }}
          className={`bg-slate-900 hover:bg-slate-800 ${
            !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div
          className={`text-center py-12 rounded-lg border-2 border-dashed ${
            isDarkMode
              ? "bg-gray-800 border-gray-600"
              : "bg-gray-50 border-gray-300"
          }`}
        >
          <ClipboardList
            className={`w-12 h-12 mx-auto mb-4 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          />
          <h3
            className={`text-lg font-medium mb-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            No Pre-Consult Templates
          </h3>
          <p
            className={`mb-4 max-w-md mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Start by creating your first pre-consultation form. Use AI to
            generate questions or build from scratch.
          </p>
          <Button
            onClick={onAddTemplate}
            className="text-white bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Template
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {template.name}
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {template.questions?.length || 0} Question
                      {template.questions?.length !== 1 ? "s" : ""}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {template.sections?.length || 0} Section
                      {template.sections?.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>

                  <p
                    className={`text-sm mb-3 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {template.description || "No description provided"}
                  </p>

                  <div
                    className={`flex items-center gap-4 text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <span>
                      Created:{" "}
                      {new Date(
                        template.createdAt || Date.now(),
                      ).toLocaleDateString()}
                    </span>
                    <span>•</span>
                    <span>Responses: {template.responseCount || 0}</span>
                    {template.shareLink && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          Public Link Available
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {template.shareLink && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        navigator.clipboard.writeText(template.shareLink!)
                      }
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditTemplate(template)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!canUpsertTemplate) {
                        setShowPermissionMessage("Create or update template");
                        setShowPermissionModal(true);
                        return;
                      }

                      onDeleteTemplate(template.id);
                    }}
                    className={`text-red-600 hover:text-red-800 ${
                      !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pre-Consult Template Creation Form Panel
export function PreConsultTemplateFormPanel({
  onSave,
  onProceedToBuilder,
  onBack,
}: PreConsultTemplateFormPanelProps) {
  const {
    consultationTemplates,
    generatePreConsultTemplate,
    darkMode: isDarkMode,
    preConsultAiTemplate,
  } = usePreConsultData();
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [gptPrompt, setGptPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
  const [selectedConsultationTemplate, setSelectedConsultationTemplate] =
    useState("");
  const [isGeneratingFromTemplate, setIsGeneratingFromTemplate] =
    useState(false);

  const previewRef = useRef<HTMLDivElement>(null);

  const [attachedDocument, setAttachedDocument] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachedDocumentBase64, setAttachedDocumentBase64] = useState<
    string | null
  >(null);
  // Scroll to preview when questions are generated
  useEffect(() => {
    if (generatedQuestions.length > 0 && previewRef.current) {
      setTimeout(() => {
        previewRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100); // Small delay to ensure the preview is rendered
    }
  }, [generatedQuestions]);
  const handleRemoveDocument = () => {
    setAttachedDocument(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleGPTGenerate = async () => {
    if (!gptPrompt.trim()) return;

    setIsGenerating(true);

    try {
      const { questions, name } = await generatePreConsultTemplate(
        gptPrompt.trim(),
        attachedDocumentBase64,
      );
      setTemplateName(name);
      setGeneratedQuestions(questions);
    } catch (error) {
      console.error("Error generating questions:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateFromConsultationTemplate = () => {
    if (!selectedConsultationTemplate) return;

    setIsGeneratingFromTemplate(true);

    // Find the selected consultation template
    const consultTemplate = consultationTemplates.find(
      (t) => t.id === selectedConsultationTemplate,
    );
    if (!consultTemplate) {
      setIsGeneratingFromTemplate(false);
      return;
    }

    // Convert consultation template questions to pre-consult format
    setTimeout(() => {
      const convertedQuestions: Question[] = [];
      let questionId = 1;
      setTemplateName(consultTemplate?.name);
      Object.entries(consultTemplate.structuredQuestions).forEach(
        ([section, questions]) => {
          questions.forEach((q) => {
            convertedQuestions.push({
              id: questionId.toString(),
              type:
                q.type === "textarea"
                  ? "text"
                  : (q.type as "text" | "single" | "multi"),
              question: q.question,
              required: q.required,
              section: section,
              options: q.options || undefined,
            });
            questionId++;
          });
        },
      );

      setGeneratedQuestions(convertedQuestions);
      setIsGeneratingFromTemplate(false);
    }, 1500);
  };

  const handleProceedToBuilder = () => {
    const templateData: PreConsultTemplate = {
      id: "TEMP-" + Date.now(),
      name: templateName,
      description: templateDescription,
      questions: generatedQuestions,
      isIncludeDisclaimer: false,
      sections:
        generatedQuestions.length > 0
          ? (Array.from(
              new Set(generatedQuestions.map((q) => q.section).filter(Boolean)),
            ) as string[])
          : defaultSections,
      active: true,
      createdAt: new Date().toISOString(),
      responseCount: 0,
    };

    // Use onProceedToBuilder if available, otherwise fall back to onSave
    if (onProceedToBuilder) {
      onProceedToBuilder(templateData);
    } else {
      onSave(templateData);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (accept PDF, DOC, DOCX, TXT)
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
      ];

      if (validTypes.includes(file.type)) {
        setAttachedDocument(file);
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result as string;
          setAttachedDocumentBase64(base64String);
        };
        reader.onerror = (error) => {
          console.error("FileReader error:", error);
        };
        reader.readAsDataURL(file);
      } else {
        alert("Please select a valid document file (PDF, DOC, DOCX, or TXT)");
      }
    }
    event.target.value = "";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`flex items-center gap-4 pb-4 border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back to Templates
        </Button>
        <div>
          <h2
            className={`text-xl font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Create Pre-Consult Template
          </h2>
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Convert consultation templates, use AI assistance, or build from
            scratch
          </p>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Template Details */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="template-name"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Template Name
            </Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., General Consultation Form"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="template-description"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Description
            </Label>
            <Textarea
              id="template-description"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Describe what this form is for and when it should be used"
              rows={3}
            />
          </div>
        </div>

        {/* Creation Options */}
        <div className="space-y-6">
          {/* Generate from Consultation Template */}
          <div
            className={`border rounded-lg p-6 ${
              isDarkMode
                ? "border-purple-700 bg-purple-900/30"
                : "border-purple-200 bg-purple-50"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <ClipboardList
                className={`w-5 h-5 ${
                  isDarkMode ? "text-purple-400" : "text-purple-600"
                }`}
              />
              <h3
                className={`font-medium ${
                  isDarkMode ? "text-purple-200" : "text-purple-900"
                }`}
              >
                Generate from Consultation Template
              </h3>
            </div>

            <p
              className={`text-sm mb-4 ${
                isDarkMode ? "text-purple-300" : "text-purple-700"
              }`}
            >
              Convert an existing consultation template into pre-consult
              questions that patients can fill out before their appointment.
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="consultation-template"
                  className={isDarkMode ? "text-purple-200" : "text-purple-900"}
                >
                  Select Consultation Template
                </Label>
                <Select
                  value={selectedConsultationTemplate}
                  onValueChange={setSelectedConsultationTemplate}
                >
                  <SelectTrigger className="relative w-full pl-3 pr-10 text-left">
                    <SelectValue
                      className="w-full text-left"
                      placeholder="Choose a consultation template..."
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {consultationTemplates.map((template) => (
                      <SelectItem
                        className="justify-start text-left"
                        key={template.id}
                        value={template.id}
                      >
                        <div>
                          <div className="flex justify-between font-medium">
                            {template.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {template.description}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedConsultationTemplate && (
                <div
                  className={`p-3 border rounded ${
                    isDarkMode
                      ? "bg-gray-800 border-purple-600"
                      : "bg-white border-purple-200"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    <strong>Preview:</strong> Questions from this consultation
                    template will be converted into patient-facing pre-consult
                    questions.
                    {(() => {
                      const template = consultationTemplates.find(
                        (t) => t.id === selectedConsultationTemplate,
                      );
                      if (!template) return null;
                      const questionCount = Object.values(
                        template.structuredQuestions,
                      ).reduce(
                        (total, questions) => total + questions.length,
                        0,
                      );
                      return ` This template contains ${questionCount} questions across ${
                        Object.keys(template.structuredQuestions).length
                      } sections.`;
                    })()}
                  </p>
                </div>
              )}

              <Button
                onClick={handleGenerateFromConsultationTemplate}
                disabled={
                  !selectedConsultationTemplate || isGeneratingFromTemplate
                }
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isGeneratingFromTemplate ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin" />
                    Converting Template...
                  </>
                ) : (
                  <>
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Generate from Template
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* AI Generation Section */}
          <div
            className={`border rounded-lg p-6 ${
              isDarkMode
                ? "border-blue-700 bg-blue-900/30"
                : "border-blue-200 bg-blue-50"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <Wand2
                className={`w-5 h-5 ${
                  isDarkMode ? "text-blue-400" : "text-blue-600"
                }`}
              />
              <h3
                className={`font-medium ${
                  isDarkMode ? "text-blue-200" : "text-blue-900"
                }`}
              >
                AI Question Generator
              </h3>
            </div>

            <p
              className={`text-sm mb-4 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              Describe the type of consultation or medical condition, and AI
              will generate relevant questions for your form.
            </p>

            <div className="space-y-4">
              <Textarea
                value={gptPrompt}
                onChange={(e) => setGptPrompt(e.target.value)}
                placeholder="e.g., 'Create questions for a dermatology consultation focusing on skin conditions and symptoms' or 'Generate questions for a general health assessment including medical history and current concerns'"
                rows={4}
                className={
                  isDarkMode
                    ? "bg-gray-800 border-blue-600 text-white placeholder-gray-400"
                    : "bg-white border-blue-200"
                }
              />

              {/* Document Attachment Section */}
              <div className="space-y-2">
                <Label
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-blue-200" : "text-blue-900"
                  }`}
                >
                  Attach Document (Optional)
                </Label>
                <p
                  className={`text-xs mb-2 ${
                    isDarkMode ? "text-blue-300" : "text-blue-700"
                  }`}
                >
                  Upload a document for AI to analyze and generate questions
                  from (PDF, DOC, DOCX, TXT)
                </p>

                {attachedDocument ? (
                  <div
                    className={`flex items-center gap-3 p-3 border rounded-lg ${
                      isDarkMode
                        ? "bg-gray-800 border-blue-600"
                        : "bg-white border-blue-200"
                    }`}
                  >
                    <FileText
                      className={`w-5 h-5 ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {attachedDocument.name}
                      </p>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {(attachedDocument.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveDocument}
                      className={`${
                        isDarkMode
                          ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          : "text-red-600 hover:text-red-700 hover:bg-red-50"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="document-upload"
                    />
                    <label htmlFor="document-upload">
                      <Button
                        type="button"
                        variant="outline"
                        // onClick={() => fileInputRef.current?.click()}
                        className={`w-full ${
                          isDarkMode
                            ? "border-blue-600 text-blue-300 hover:bg-blue-900/20"
                            : "border-blue-300 text-blue-700 hover:bg-blue-50"
                        }`}
                        asChild
                      >
                        <div className="cursor-pointer">
                          <Paperclip className="w-4 h-4 mr-2" />
                          Attach Document
                        </div>
                      </Button>
                    </label>
                  </div>
                )}
              </div>

              <Button
                onClick={handleGPTGenerate}
                disabled={!gptPrompt.trim() || isGenerating}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isGenerating ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Questions
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Generated Questions Preview */}
        {generatedQuestions.length > 0 && (
          <div
            ref={previewRef}
            className={`border rounded-lg p-6 ${
              isDarkMode
                ? "border-green-700 bg-green-900/30"
                : "border-green-200 bg-green-50"
            }`}
          >
            <h3
              className={`font-medium mb-4 flex items-center gap-2 ${
                isDarkMode ? "text-green-200" : "text-green-900"
              }`}
            >
              <CheckCircle className="w-5 h-5" />
              Preview Questions ({generatedQuestions.length})
            </h3>

            <div className="mb-6 space-y-3">
              {generatedQuestions.slice(0, 3).map((q, index) => (
                <div
                  key={q.id}
                  className={`border rounded p-3 ${
                    isDarkMode
                      ? "bg-gray-800 border-green-600"
                      : "bg-white border-green-200"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {q.type === "text"
                        ? "Free Text"
                        : q.type === "single"
                          ? "Single Choice"
                          : "Multi Choice"}
                    </Badge>
                    {q.section && (
                      <Badge variant="secondary" className="text-xs">
                        {q.section}
                      </Badge>
                    )}
                  </div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    {q.question}
                  </p>
                  {q.options && (
                    <p
                      className={`text-xs mt-1 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Options: {q.options.slice(0, 2).join(", ")}
                      {q.options.length > 2 &&
                        ` (+${q.options.length - 2} more)`}
                    </p>
                  )}
                </div>
              ))}
              {generatedQuestions.length > 3 && (
                <p
                  className={`text-sm text-center py-2 ${
                    isDarkMode ? "text-green-300" : "text-green-700"
                  }`}
                >
                  +{generatedQuestions.length - 3} more questions generated
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleProceedToBuilder}
                disabled={!templateName?.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                Proceed to Form Builder
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setGeneratedQuestions([]);
                  setSelectedConsultationTemplate("");
                  setGptPrompt("");
                }}
                className="text-green-700 border-green-300"
              >
                Clear Questions
              </Button>
            </div>
          </div>
        )}

        {/* Manual Creation Option */}
        {generatedQuestions.length === 0 && (
          <div
            className={`border rounded-lg p-6 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800/30"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <Plus
                className={`w-5 h-5 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              />
              <h3
                className={`font-medium ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Start with Blank Template
              </h3>
            </div>
            <p
              className={`text-sm mb-4 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Build your form from scratch using the form builder. Add questions
              manually and customize the structure to your exact needs.
            </p>
            <Button
              onClick={() => handleProceedToBuilder()}
              disabled={!templateName.trim()}
              variant="outline"
              className={
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : ""
              }
            >
              <Plus className="w-4 h-4 mr-2" />
              Open Form Builder
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Pre-Consult Template Builder Panel
export function PreConsultTemplateBuilderPanel({
  template,
  onSave,
  onBack,
  confirmDelete,
}: PreConsultTemplateBuilderPanelProps) {
  const {
    isUpdatePreConsultTemplateLoading,
    isSavePreConsultTemplateLoading,
    darkMode: isDarkMode,
  } = usePreConsultData();

  const [formData, setFormData] = useState<PreConsultTemplate>(
    template || {
      id: null,
      name: "",
      description: "",
      questions: [],
      sections: defaultSections,
      active: true,
      isIncludeDisclaimer: false,
    },
  );

  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | undefined>(
    undefined,
  );
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(formData.sections),
  );

  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const questionsBySection = useMemo(() => {
    const grouped = formData.sections.reduce(
      (acc, section) => {
        acc[section] = formData.questions.filter((q) => q.section === section);
        return acc;
      },
      {} as Record<string, Question[]>,
    );

    const unassignedQuestions = formData.questions.filter(
      (q) => !q.section || !formData.sections.includes(q.section),
    );
    if (unassignedQuestions.length > 0 && formData.sections.length > 0) {
      grouped[formData.sections[0]] = [
        ...(grouped[formData.sections[0]] || []),
        ...unassignedQuestions,
      ];
    }

    return grouped;
  }, [formData.questions, formData.sections]);

  const handleSectionsUpdate = (
    newSections: string[],
    oldSectionName?: string,
    newSectionName?: string,
  ) => {
    setFormData((prev) => {
      let updatedQuestions = prev.questions;

      if (oldSectionName && newSectionName) {
        updatedQuestions = prev.questions.map((q) => ({
          ...q,
          section: q.section === oldSectionName ? newSectionName : q.section,
        }));
      } else {
        updatedQuestions = prev.questions.map((q) => ({
          ...q,
          section: newSections.includes(q.section || "")
            ? q.section
            : newSections[0],
        }));
      }

      return {
        ...prev,
        sections: newSections,
        questions: updatedQuestions,
      };
    });
  };
  const handleSectionsUpdateCallback = (newSections: string[]) => {
    const newQuestions = newSections.flatMap((section) =>
      formData.questions.filter((q) => q.section === section),
    );

    setFormData((prev) => ({
      ...prev,
      sections: newSections,
      questions: newQuestions,
    }));
  };

  const handleAddQuestion = (section?: string) => {
    setEditingQuestion(undefined);
    setShowQuestionForm(true);
    // Pre-select section if provided
    if (section && formData.sections.includes(section)) {
      setEditingQuestion({
        id: "",
        type: "text",
        question: "",
        section: section,
        required: false,
        options: [],
      });
    }
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setShowQuestionForm(true);
  };

  const handleQuestionSave = (questionData: Question) => {
    let targetId: string | null = null;

    if (editingQuestion && editingQuestion.id) {
      setFormData((prev) => ({
        ...prev,
        questions: prev.questions.map((q) =>
          q.id === editingQuestion.id
            ? { ...questionData, id: editingQuestion.id }
            : q,
        ),
      }));

      setExpandedSections((prev) => {
        const s = new Set(prev);
        if (questionData.section) s.add(questionData.section);
        return s;
      });

      targetId = editingQuestion.id;
    } else {
      const newQuestion = {
        ...questionData,
        id: Date.now().toString(),
      };
      setFormData((prev) => ({
        ...prev,
        questions: [...prev.questions, newQuestion],
      }));

      // Expand section and scroll to the new question
      setExpandedSections((prev) => {
        const s = new Set(prev);
        if (newQuestion.section) s.add(newQuestion.section);
        return s;
      });

      targetId = newQuestion.id;
    }

    // Close editor state
    setShowQuestionForm(false);
    setEditingQuestion(undefined);

    // Scroll to the saved question using refs. Use a small timeout to allow DOM update.
    if (targetId) {
      setTimeout(() => {
        const el = questionRefs.current[targetId!];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
    }
  };

  const handleQuestionDelete = (questionId: string) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((q) => q.id !== questionId),
    }));
  };

  const handleQuestionMove = (
    questionId: string,
    direction: "up" | "down",
    section: string,
  ) => {
    const sectionQuestions = questionsBySection[section];
    const currentIndex = sectionQuestions.findIndex((q) => q.id === questionId);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < sectionQuestions.length) {
      const newQuestions = [...formData.questions];
      const questionAIndex = newQuestions.findIndex(
        (q) => q.id === sectionQuestions[currentIndex].id,
      );
      const questionBIndex = newQuestions.findIndex(
        (q) => q.id === sectionQuestions[newIndex].id,
      );

      [newQuestions[questionAIndex], newQuestions[questionBIndex]] = [
        newQuestions[questionBIndex],
        newQuestions[questionAIndex],
      ];

      setFormData((prev) => ({ ...prev, questions: newQuestions }));
    }
  };
  const handleSectionMove = (sectionName: string, direction: "up" | "down") => {
    const currentIndex = formData.sections.indexOf(sectionName);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < formData.sections.length) {
      // Swap sections
      const newSections = [...formData.sections];
      [newSections[currentIndex], newSections[newIndex]] = [
        newSections[newIndex],
        newSections[currentIndex],
      ];

      // Reorder questions based on new section order
      const newQuestions = newSections.flatMap((section) =>
        formData.questions.filter((q) => q.section === section),
      );

      setFormData((prev) => ({
        ...prev,
        sections: newSections,
        questions: newQuestions,
      }));
    }
  };

  const toggleSectionExpanded = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleSave = () => {
    onSave(formData);
  };

  if (showQuestionForm) {
    return (
      <QuestionFormPanel
        question={editingQuestion}
        sections={formData.sections}
        onSave={handleQuestionSave}
        onBack={() => setShowQuestionForm(false)}
      />
    );
  }

  const canUpsertTemplate = checkUserPermissions(
    accessTitanverseTemplateUpsert,
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`flex items-center justify-between pb-4 border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className={
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-gray-700"
                : ""
            }
          >
            ← Back to Templates
          </Button>
          <div>
            <h2
              className={`text-xl font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {template ? "Edit" : "Build"} Pre-Consult Template
            </h2>
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {formData.name || "Untitled Template"}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => {
              if (canUpsertTemplate) {
                handleSave();
              } else {
                setShowPermissionMessage("Create or update template");
                setShowPermissionModal(true);
              }
            }}
            className={`bg-slate-900 hover:bg-slate-800 ${
              !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
            }`}
            // keep existing validation as disabled if you want true form validation
            disabled={
              !formData.name ||
              formData.questions.length === 0 ||
              isUpdatePreConsultTemplateLoading ||
              isSavePreConsultTemplateLoading
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {isUpdatePreConsultTemplateLoading ||
            isSavePreConsultTemplateLoading
              ? "Processing..."
              : "Save Template"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Form Builder */}
        <div className="space-y-6 lg:col-span-2">
          {/* Template Settings */}
          <div
            className={`border rounded-lg p-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h3
              className={`font-medium mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Template Settings
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  Template Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter template name"
                  className={`transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="description"
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe this template"
                  rows={2}
                  className={`transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, active: checked }))
                  }
                />
                <Label
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  Active (patients can access this form)
                </Label>
              </div>
            </div>
          </div>

          {/* Sections and Questions */}
          <div
            className={`border rounded-lg p-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`font-medium ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Form Structure ({formData.questions.length} Questions across{" "}
                {formData.sections.length} Sections)
              </h3>
              <div className="flex gap-2">
                <SectionManager
                  sections={formData.sections}
                  onSectionsUpdate={handleSectionsUpdate}
                  isDarkMode={isDarkMode}
                  onSectionsUpdateCallback={handleSectionsUpdateCallback}
                />
              </div>
            </div>

            {formData.sections.length === 0 ? (
              <div
                className={`text-center py-8 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <FolderPlus
                  className={`w-8 h-8 mx-auto mb-2 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                />
                <p>No sections created yet</p>
                <p className="text-sm">Use "Manage Sections" to add sections</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.sections.map((section, sectionIndex) => {
                  const sectionQuestions = questionsBySection[section] || [];
                  const isExpanded = expandedSections.has(section);

                  return (
                    <div
                      key={section}
                      className={`border rounded-lg ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      {/* Section Header */}
                      <div
                        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-opacity-80 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        }`}
                        onClick={() => toggleSectionExpanded(section)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            <Folder
                              className={`w-4 h-4 ${
                                isDarkMode ? "text-blue-400" : "text-blue-600"
                              }`}
                            />
                          </div>
                          <div>
                            <h4
                              className={`font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {section}
                            </h4>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {sectionQuestions.length} question
                              {sectionQuestions.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSectionMove(section, "up");
                            }}
                            disabled={sectionIndex === 0}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSectionMove(section, "down");
                            }}
                            disabled={
                              sectionIndex === formData.sections.length - 1
                            }
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddQuestion(section);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Section Questions */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-600">
                          {sectionQuestions.length === 0 ? (
                            <div
                              className={`text-center py-6 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              <p className="text-sm">
                                No questions in this section
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddQuestion(section)}
                                className="mt-2 text-blue-600 hover:text-blue-800"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add First Question
                              </Button>
                            </div>
                          ) : (
                            <div className="p-4 space-y-3">
                              {sectionQuestions.map(
                                (question, questionIndex) => (
                                  <div
                                    key={question.id}
                                    ref={(el) =>
                                      (questionRefs.current[question.id] = el)
                                    }
                                    className={`border rounded-lg p-3 ${
                                      isDarkMode
                                        ? "border-gray-600 bg-gray-700"
                                        : "border-gray-200 bg-white"
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Badge
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            {question.type === "text"
                                              ? "Free Text"
                                              : question.type === "single"
                                                ? "Single Choice"
                                                : question.type === "multi"
                                                  ? "Multi Choice"
                                                  : question.type === "image"
                                                    ? "Image Upload"
                                                    : "Unknown Type"}
                                          </Badge>
                                          {question.required && (
                                            <Badge
                                              variant="destructive"
                                              className="text-xs"
                                            >
                                              Required
                                            </Badge>
                                          )}
                                        </div>
                                        <p
                                          className={`font-medium mb-1 ${
                                            isDarkMode
                                              ? "text-white"
                                              : "text-gray-900"
                                          }`}
                                        >
                                          <span className="mr-2 text-blue-600">
                                            {questionIndex + 1}.
                                          </span>
                                          {question.question}
                                        </p>
                                        {question.options && (
                                          <p
                                            className={`text-sm ${
                                              isDarkMode
                                                ? "text-gray-300"
                                                : "text-gray-600"
                                            }`}
                                          >
                                            Options:{" "}
                                            {question.options
                                              .slice(0, 3)
                                              .join(", ")}
                                            {question.options.length > 3 &&
                                              ` (+${
                                                question.options.length - 3
                                              } more)`}
                                          </p>
                                        )}
                                      </div>

                                      <div className="flex items-center gap-1 ml-4">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleQuestionMove(
                                              question.id,
                                              "up",
                                              section,
                                            )
                                          }
                                          disabled={questionIndex === 0}
                                        >
                                          <ArrowUp className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleQuestionMove(
                                              question.id,
                                              "down",
                                              section,
                                            )
                                          }
                                          disabled={
                                            questionIndex ===
                                            sectionQuestions.length - 1
                                          }
                                        >
                                          <ArrowDown className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleEditQuestion(question)
                                          }
                                          className="text-blue-600 hover:text-blue-800"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            confirmDelete(
                                              () =>
                                                handleQuestionDelete(
                                                  question.id,
                                                ),
                                              deleteConfirmationMessage +
                                                "question",
                                            );
                                          }}
                                          className="text-red-600 hover:text-red-800"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="space-y-6">
          <div
            className={`border rounded-lg p-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h3
              className={`font-medium mb-4 flex items-center gap-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <Eye className="w-4 h-4" />
              Form Preview
            </h3>

            <div className="space-y-4 text-sm">
              <div
                className={`p-3 border rounded ${
                  isDarkMode
                    ? "bg-blue-900/30 border-blue-700"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <h4
                  className={`font-medium ${
                    isDarkMode ? "text-blue-200" : "text-blue-900"
                  }`}
                >
                  {formData.name || "Untitled Template"}
                </h4>
                <p
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-blue-300" : "text-blue-700"
                  }`}
                >
                  {formData.description || "No description"}
                </p>
              </div>

              {/* Preview by sections */}
              {formData.sections.map((section) => {
                const sectionQuestions = questionsBySection[section] || [];
                if (sectionQuestions.length === 0) return null;

                return (
                  <div key={section} className="space-y-2">
                    <div
                      className={`px-2 py-1 text-xs font-medium border-l-4 ${
                        isDarkMode
                          ? "bg-gray-800 border-blue-500 text-gray-200"
                          : "bg-gray-100 border-blue-500 text-gray-700"
                      }`}
                    >
                      {section}
                    </div>
                    {sectionQuestions.slice(0, 2).map((q, index) => (
                      <div
                        key={q.id}
                        className={`p-3 border rounded text-xs ml-4 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <p
                          className={`font-medium mb-1 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {index + 1}. {q.question}
                          {q.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </p>
                        {q.type === "text" && (
                          <div
                            className={`w-full h-6 border rounded ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-white border-gray-300"
                            }`}
                          ></div>
                        )}
                        {q.type === "single" && q.options && (
                          <div className="space-y-1">
                            {q.options.slice(0, 2).map((option, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div
                                  className={`w-3 h-3 border rounded-full ${
                                    isDarkMode
                                      ? "border-gray-500"
                                      : "border-gray-400"
                                  }`}
                                ></div>
                                <span
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-900"
                                  }`}
                                >
                                  {option}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {sectionQuestions.length > 2 && (
                      <p
                        className={`text-xs text-center py-1 ml-4 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        +{sectionQuestions.length - 2} more in this section
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Disclaimer Preview */}
              {formData.isIncludeDisclaimer && (
                <div className="space-y-2 mt-4 pt-4 border-t">
                  <div
                    className={`px-2 py-1 text-xs font-medium border-l-4 ${
                      isDarkMode
                        ? "bg-gray-800 border-amber-500 text-gray-200"
                        : "bg-gray-100 border-amber-500 text-gray-700"
                    }`}
                  >
                    Disclaimer
                  </div>
                  <div
                    className={`p-3 border rounded text-xs ml-4 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <p
                      className={`mb-3 whitespace-pre-wrap ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {formData.disclaimerText || "No disclaimer text provided"}
                    </p>
                    <div className="space-y-2 mt-4 pt-3 border-t border-gray-300">
                      <Label
                        className={`text-xs font-medium ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Patient Signature{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div
                        className={`w-full h-16 border-2 border-dashed rounded flex items-center justify-center ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-gray-400"
                            : "bg-white border-gray-300 text-gray-400"
                        }`}
                      >
                        <span className="text-xs">Signature box</span>
                      </div>
                      <div className="flex items-start gap-2 mt-2">
                        <div
                          className={`w-3 h-3 mt-0.5 border rounded ${
                            isDarkMode ? "border-gray-500" : "border-gray-400"
                          }`}
                        ></div>
                        <Label
                          className={`text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          I acknowledge that I have read and understood the
                          disclaimer above
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            className={`border rounded-lg p-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h3
              className={`font-medium mb-3 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Share Options
            </h3>
            <div className="space-y-3 text-sm">
              <div
                className={`flex items-center justify-between p-2 rounded ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <span
                  className={isDarkMode ? "text-gray-300" : "text-gray-900"}
                >
                  Public Link
                </span>
                <Switch checked={formData.active} disabled />
              </div>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                When active, patients can access this form via a shareable link.
              </p>
              <div className="border-t pt-4 mt-4 space-y-3">
                <div
                  className={`flex items-center justify-between p-2 rounded ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <span
                    className={isDarkMode ? "text-gray-300" : "text-gray-900"}
                  >
                    Include Disclaimer
                  </span>
                  <Switch
                    checked={formData.isIncludeDisclaimer || false}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        isIncludeDisclaimer: checked,
                      }))
                    }
                  />
                </div>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Add a disclaimer section at the end of the form with a
                  signature box for patients.
                </p>

                {formData.isIncludeDisclaimer && (
                  <div className="space-y-2 mt-3">
                    <Label
                      htmlFor="disclaimerText"
                      className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                    >
                      Disclaimer Text
                    </Label>
                    <Textarea
                      id="disclaimerText"
                      value={formData.disclaimerText || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          disclaimerText: e.target.value,
                        }))
                      }
                      placeholder="Enter the disclaimer text that patients must read and sign..."
                      rows={6}
                      className={
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                          : ""
                      }
                    />
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      This disclaimer will appear at the end of the pre-consult
                      form along with a signature box.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Question Form Panel
export function QuestionFormPanel({
  question,
  sections,
  onSave,
  onBack,
}: QuestionFormPanelProps) {
  const { darkMode: isDarkMode } = usePreConsultData();

  const [formData, setFormData] = useState<Question>(
    question || {
      id: "",
      type: "text",
      question: "",
      section:
        sections && sections.length > 0 ? sections[0] : "General Information",
      required: false,
      options: [],
    },
  );

  const [newOption, setNewOption] = useState("");

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFormData((prev) => ({
        ...prev,
        options: [...(prev.options || []), newOption.trim()],
      }));
      setNewOption("");
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!formData.question.trim()) return;

    const questionData: any = { ...formData };
    if (formData.type === "text") {
      questionData.options = undefined;
    }

    onSave(questionData);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`flex items-center gap-4 pb-4 border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back to Form Builder
        </Button>
        <div>
          <h2
            className={`text-xl font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {question ? "Edit" : "Add"} Question
          </h2>
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Configure your form question and response options
          </p>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Question Details */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="question"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Question Text
            </Label>
            <div>
              <Textarea
                id="question"
                value={formData.question}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, question: e.target.value }))
                }
                placeholder="Enter your question here..."
                rows={3}maxLength={5000} 
                className={formData.question.length > 5000 ? "border-red-500" : ""}
              />
              
              <div className="flex justify-between mt-1 text-xs">
                {formData.question.length > 5000 ? (
                  <span className="text-red-500 font-medium">
                    You have exceeded the 5000 character limit.
                  </span>
                ) : (
                  <span className="text-slate-500">
                    Maximum 5000 characters
                  </span>
                )}
                
                <span className={formData.question.length > 5000 ? "text-red-500" : "text-slate-500"}>
                  {formData.question.length} / 5000
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="type"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                Question Type
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value: "text" | "single" | "multi" | "image") =>
                  setFormData((prev) => ({
                    ...prev,
                    type: value,
                    options:
                      value === "text" || value === "image" ? [] : prev.options,
                  }))
                }
              >
                <SelectTrigger
                  className={`transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Free Text</SelectItem>
                  <SelectItem value="single">Single Choice</SelectItem>
                  <SelectItem value="multi">Multi Choice</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="section"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                Section
              </Label>
              <Select
                value={formData.section}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, section: value }))
                }
              >
                <SelectTrigger
                  className={`transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(sections && sections.length > 0
                    ? sections
                    : ["General Information"]
                  ).map((section) => (
                    <SelectItem key={section} value={section}>
                      {section}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.required}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, required: checked }))
              }
            />
            <Label className={isDarkMode ? "text-gray-200" : "text-gray-900"}>
              Required field
            </Label>
          </div>
        </div>

        {/* Options for Single/Multi Choice */}
        {(formData.type === "single" || formData.type === "multi") && (
          <div className="space-y-4">
            <Label className={isDarkMode ? "text-gray-200" : "text-gray-900"}>
              Response Options
            </Label>

            {formData.options && formData.options.length > 0 && (
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-2 border rounded ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-800"
                        : "border-gray-200"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 border-2 ${
                        formData.type === "single" ? "rounded-full" : "rounded"
                      } ${isDarkMode ? "border-gray-500" : "border-gray-400"}`}
                    ></div>
                    <span
                      className={`flex-1 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      {option}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveOption(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Add an option..."
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
              />
              <Button
                onClick={handleAddOption}
                disabled={!newOption.trim()}
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
        )}

        {/* Preview */}
        <div
          className={`border rounded-lg p-4 ${
            isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <h3
            className={`font-medium mb-3 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Preview
          </h3>
          <div className="space-y-3">
            <p
              className={`font-medium ${
                isDarkMode ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {formData.question || "Your question will appear here..."}
              {formData.required && (
                <span className="ml-1 text-red-500">*</span>
              )}
            </p>

            {formData.type === "text" && (
              <Textarea
                placeholder="Patient's response will be typed here..."
                disabled
                rows={3}
                className={
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-gray-300"
                    : "bg-white"
                }
              />
            )}

            {formData.type === "single" && (
              <div className="space-y-2">
                {formData.options && formData.options.length > 0 ? (
                  formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input type="radio" name="preview" disabled />
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-900"
                        }
                      >
                        {option}
                      </span>
                    </div>
                  ))
                ) : (
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Add options above to see them here
                  </p>
                )}
              </div>
            )}

            {formData.type === "multi" && (
              <div className="space-y-2">
                {formData.options && formData.options.length > 0 ? (
                  formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input type="checkbox" disabled />
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-900"
                        }
                      >
                        {option}
                      </span>
                    </div>
                  ))
                ) : (
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Add options above to see them here
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onBack}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !formData.question.trim() ||
              (formData.type !== "text" &&
                formData.type !== "image" &&
                (!formData.options || formData.options.length === 0))
            }
            className="bg-slate-900 hover:bg-slate-800"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Question
          </Button>
        </div>
      </div>
    </div>
  );
}
